import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  IconButton,
  Divider,
  Card,
  CardContent,
  useTheme,
  alpha,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Switch,
  Slider,
  Radio,
  RadioGroup,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import 'react-quill-new/dist/quill.snow.css';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Code as VariableIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
  List as ListIcon,
  SmartButton as ButtonIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  BoltOutlined as TriggerIcon,
  EventOutlined as DayIcon,
  FlashOnOutlined as OnCreateIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import masterEmailService, { MasterEmail as ApiMasterEmail } from '../../services/masterEmailService';

// Types
interface MasterEmail extends Omit<ApiMasterEmail, 'id'> {
  id: string; // Use string for frontend compatibility, maps to _id
}

const DEFAULT_EMAIL_LAYOUT = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      padding: 0;
      background-color: #f4f6f8;
      font-family: Arial, Helvetica, sans-serif;
      width: 100%;
      overflow-x: hidden;
    }
    .email-container {
      width: 100%;
      background-color: #f4f6f8;
      padding: 20px 0;
      text-align: center;
    }
    .email-content {
      width: 100%;
      max-width: 600px;
      margin: 0 auto;
      background-color: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 6px rgba(0,0,0,0.1);
      text-align: left; /* Reset text alignment for content */
    }
    .header {
      background-color: #2f6fed;
      color: #ffffff;
      padding: 16px 20px;
      text-align: center;
    }
    .body {
      padding: 24px 30px;
      color: #333333;
      font-size: 15px;
      line-height: 1.5;
    }
    .footer {
      background-color: #f0f2f5;
      text-align: center;
      padding: 16px;
      font-size: 12px;
      color: #777777;
    }
    /* Quill specific overrides */
    .body p {
      margin: 0 0 0.5em 0;
      min-height: 0.5em;
    }
    .body p:last-child { margin-bottom: 0; }
    .body strong { font-weight: bold; }
    .body br { display: block; content: ""; margin-top: 5px; }
    .body img { max-width: 100%; height: auto; display: block; margin: 10px 0; }
    .ql-align-center { text-align: center; }
    .ql-align-right { text-align: right; }
    .ql-align-justify { text-align: justify; }

    /* CTA Button Styles for Editor View */
    .cta-button {
      text-decoration: none;
      display: inline-block;
      text-align: center;
      font-weight: bold;
    }
  </style>
</head>
<body>
  <div class="email-container">
    <div class="email-content">
      <div class="header">
        <h1 style="margin:0; font-size:24px;">{{layout_title}}</h1>
      </div>
      <div class="body">
        {{content}}
        {{cta_placeholder}}
      </div>
      <div class="footer">
        © 2026 JobHunter. All rights reserved.
      </div>
    </div>
  </div>
</body>
</html>
`;


// Sortable Row Component
interface SortableRowProps {
  id: string;
  children: React.ReactNode;
}

const SortableRow = ({ id, children }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    backgroundColor: isDragging ? '#f0f0f0' : 'inherit',
    zIndex: isDragging ? 2 : 1,
    position: 'relative' as 'relative',
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      hover
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && (child.props as any).className === 'drag-handle') {
          return React.cloneElement(child as React.ReactElement<any>, { ...attributes, ...listeners });
        }
        return child;
      })}
    </TableRow>
  );
};


const MasterEmailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Navigation State
  const [view, setView] = useState<'list' | 'edit'>('list');
  const quillRef = useRef<any>(null);
  const [templates, setTemplates] = useState<MasterEmail[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MasterEmail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [dynamicVariables, setDynamicVariables] = useState<{ name: string; value: string }[]>([]);

  React.useEffect(() => {
    fetchTemplates();
    fetchVariables();
    setView('list');
  }, []);

  const fetchTemplates = async () => {
    setIsLoading(true);
    try {
      const data = await masterEmailService.getAllMasterEmails();
      // Map _id to id for frontend compatibility
      const mappedData = data.map(t => ({ ...t, id: t._id || t.id || '' }));
      setTemplates(mappedData);
    } catch (error) {
      console.error('Error fetching master emails:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVariables = async () => {
    try {
      const data = await masterEmailService.getVariables();
      setDynamicVariables(data);
    } catch (error) {
      console.error('Error fetching variables:', error);
    }
  };

  // Button State (Integrated in layout)
  const [showCTA, setShowCTA] = useState(false);
  const [buttonConfig, setButtonConfig] = useState({
    text: 'Click Here',
    url: 'https://',
    bgColor: '#2f6fed',
    textColor: '#ffffff',
    borderRadius: '4',
    width: '200',
    height: '45',
    alignment: 'center',
    vPosition: 100, // 0-100% vertical position
    marginTop: '20',
    marginBottom: '20',
  });

  // Editor State
  const [templateTitle, setTemplateTitle] = useState('');
  const [description, setDescription] = useState('');
  const [layoutTitle, setLayoutTitle] = useState('Welcome to JobHunter 🎉');
  const [slug, setSlug] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [editorLayout, setEditorLayout] = useState<'split' | 'full-preview'>('split');
  const [isSimpleEmail, setIsSimpleEmail] = useState(false);

  // Trigger State
  const [triggerType, setTriggerType] = useState<'on_create' | 'day'>('on_create');
  const [triggerDays, setTriggerDays] = useState('1');

  // Variable Menu State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openVariableMenu = Boolean(anchorEl);

  // Test Email State
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Deletion State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<MasterEmail | null>(null);

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setTemplates((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);

        // Persist to backend
        const orderedIds = newItems.map(item => item.id);
        masterEmailService.reorderMasterEmails(orderedIds).catch(err => {
          console.error("Failed to save order", err);
        });

        return newItems;
      });
    }
  };

  const handleEditClick = async (template: MasterEmail) => {
    try {
      setSubmitLoader(true);
      const templateId = template.id || template._id;
      if (!templateId) return;

      const fullTemplate = await masterEmailService.getMasterEmailById(templateId);

      // Ensure the template has a consistent ID property for handleSave
      const templateToSet = {
        ...fullTemplate,
        id: fullTemplate.id || (fullTemplate as any)._id
      };

      setSelectedTemplate(templateToSet as any);
      setTemplateTitle(fullTemplate.title);
      setDescription(fullTemplate.description || '');
      setLayoutTitle(fullTemplate.layoutTitle || 'Welcome to JobHunter 🎉');
      setSlug(fullTemplate.slug || '');
      setSubject(fullTemplate.subject);
      setContent(fullTemplate.content);

      // Button config
      if (fullTemplate.buttonConfig) {
        setButtonConfig(fullTemplate.buttonConfig);
        setShowCTA(fullTemplate.showCTA);
      } else {
        setShowCTA(false);
        setButtonConfig({
          text: 'Click Here',
          url: 'https://',
          bgColor: '#2f6fed',
          textColor: '#ffffff',
          borderRadius: '4',
          width: '200',
          height: '45',
          alignment: 'center',
          vPosition: 100,
          marginTop: '20',
          marginBottom: '20',
        });
      }

      setIsSimpleEmail(!!fullTemplate.is_simple_email);

      // Trigger config
      setTriggerType(fullTemplate.triggerType || 'on_create');
      setTriggerDays(
        fullTemplate.triggerDays !== undefined && fullTemplate.triggerDays !== null
          ? String(fullTemplate.triggerDays)
          : '1'
      );

      setView('edit');
      setFormErrors({});
    } catch (error) {
      console.error('Error fetching master email details:', error);
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleAddNew = () => {
    setSelectedTemplate(null);
    setTemplateTitle('');
    setDescription('');
    setLayoutTitle('Welcome to JobHunter 🎉');
    setSlug('');
    setSubject('');
    setContent('');
    setShowCTA(false);
    setButtonConfig({
      text: 'Click Here',
      url: 'https://',
      bgColor: '#2f6fed',
      textColor: '#ffffff',
      borderRadius: '4',
      width: '200',
      height: '45',
      alignment: 'center',
      vPosition: 100,
      marginTop: '20',
      marginBottom: '20',
    });
    setIsSimpleEmail(false);
    setTriggerType('on_create');
    setTriggerDays('1');
    setView('edit');
    setFormErrors({});
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedTemplate(null);
    setTemplateTitle('');
    setDescription('');
    setSlug('');
    setSubject('');
    setContent('');
    setShowCTA(false);
    setTriggerType('on_create');
    setTriggerDays('1');
    setFormErrors({});
  };

  const handleVariableClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleVariableClose = () => {
    setAnchorEl(null);
  };

  const insertVariable = (variable: string) => {
    const formattedVar = `{{${variable}}}`;
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      const range = editor.getSelection();
      if (range) {
        editor.insertText(range.index, formattedVar);
      } else {
        editor.insertText(editor.getLength() - 1, formattedVar);
      }
    } else {
      setContent(prev => prev + ' ' + formattedVar);
    }
    handleVariableClose();
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!templateTitle.trim()) {
      errors.templateTitle = 'Template Name is required';
    }

    if (!slug.trim()) {
      errors.slug = 'Unique Identifier (Slug) is required';
    }

    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!content.trim() || content === '<p><br></p>') {
      errors.content = 'Email Content is required';
    }

    if (triggerType === 'day') {
      const days = parseInt(triggerDays, 10);
      if (isNaN(days) || days < 0) {
        errors.triggerDays = 'Enter a valid number of days (0 or more)';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const templateData = {
      title: templateTitle,
      description,
      slug,
      subject,
      content,
      layoutTitle,
      showCTA,
      buttonConfig: showCTA ? buttonConfig : undefined,
      fullHtml: getTemplateHtml(), // Insert full HTML
      is_simple_email: isSimpleEmail,
      triggerType,
      triggerDays: triggerType === 'day' ? (parseInt(triggerDays, 10) || 0) : 0,
    };

    try {
      setSubmitLoader(true);
      if (selectedTemplate && selectedTemplate.id) {
        await masterEmailService.updateMasterEmail(selectedTemplate.id, templateData as any);
      } else {
        await masterEmailService.createMasterEmail({ ...templateData, title: templateData.title || subject } as any);
      }
      await fetchTemplates();
      handleBackToList();
    } catch (error) {
      console.error('Error saving master email:', error);
    } finally {
      setSubmitLoader(false);
    }
  };

  const handleDeleteClick = (template: MasterEmail) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      setSubmitLoader(true);
      await masterEmailService.deleteMasterEmail(templateToDelete.id);
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting master email:', error);
    } finally {
      setSubmitLoader(false);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleOpenTestEmailDialog = () => {
    setTestEmailOpen(true);
  };

  const handleCloseTestEmailDialog = () => {
    setTestEmailOpen(false);
    setTestEmailAddress('');
  };

  const handleSendTestEmailConfirm = async () => {
    if (!testEmailAddress) {
      alert('Please enter an email address');
      return;
    }

    setIsSendingTest(true);
    try {
      const htmlContent = getTemplateHtml();
      await masterEmailService.sendTestEmail(testEmailAddress, subject, htmlContent);
      handleCloseTestEmailDialog();
    } catch (error) {
      console.error('Failed to send test email:', error);
    } finally {
      setIsSendingTest(false);
    }
  };

  const getTemplateHtml = () => {
    let renderedContent = content;

    // 1. Logic to inject visual CTA button based on vPosition (Only if not simple email)
    if (showCTA) {
      const { text, url, bgColor, textColor, borderRadius, width, height, alignment, vPosition, marginTop, marginBottom } = buttonConfig;

      const buttonTable = `
        <table width="100%" border="0" cellspacing="0" cellpadding="0" style="margin-top: ${marginTop}px; margin-bottom: ${marginBottom}px;">
          <tr>
            <td align="${alignment}">
              <table border="0" cellspacing="0" cellpadding="0">
                <tr>
                  <td align="center" style="border-radius: ${borderRadius}px;" bgcolor="${bgColor}">
                    <a href="${url}" target="_blank" style="font-size: 16px; font-family: Arial, sans-serif; color: ${textColor}; text-decoration: none; border-radius: ${borderRadius}px; padding: 0 20px; border: 1px solid ${bgColor}; display: inline-block; font-weight: bold; line-height: ${height}px; width: ${width}px; text-align: center;">
                      ${text}
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      `;

      // Split content into paragraphs or breaks to find insertion point
      const blocks = renderedContent.split(/(<\/p>|<br\s*\/?>)/i).filter(b => b.trim() !== "");

      if (blocks.length > 0) {
        // Calculate insertion index
        const insertIndex = Math.floor((vPosition / 100) * blocks.length);

        // Handle edge cases (top/bottom)
        if (insertIndex === 0) {
          renderedContent = buttonTable + renderedContent;
        } else if (insertIndex >= blocks.length) {
          renderedContent = renderedContent + buttonTable;
        } else {
          // Insert at the calculated block
          const newContent: string[] = [];
          blocks.forEach((block, idx) => {
            newContent.push(block);
            if (idx === insertIndex) {
              newContent.push(buttonTable);
            }
          });
          renderedContent = newContent.join("");
        }
      } else {
        // Fallback for empty or single-line content
        renderedContent = renderedContent + buttonTable;
      }
    }

    if (isSimpleEmail) {
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 16px;
              line-height: 1.6;
              color: #333333;
              padding: 20px;
              margin: 0;
            }
            p { margin-bottom: 1em; }
          </style>
        </head>
        <body>
          <div class="content">${renderedContent}</div>
        </body>
        </html>
      `;
    }

    const finalHtml = DEFAULT_EMAIL_LAYOUT
      .replace('{{layout_title}}', layoutTitle)
      .replace('{{content}}', renderedContent)
      .replace('{{cta_placeholder}}', ''); // Placeholder is effectively ignored now

    return finalHtml;
  };

  const renderLivePreview = () => {
    const finalHtml = getTemplateHtml();

    return (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          minHeight: '600px',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: '#f4f6f8',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 1, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PreviewIcon fontSize="small" color="primary" />
          <Typography variant="caption" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
            Live Preview
          </Typography>
        </Box>
        <iframe
          srcDoc={finalHtml.replace(/&nbsp;/g, ' ')}
          title="Email Preview"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
            backgroundColor: 'transparent'
          }}
        />
      </Box>
    );
  };

  const describeTrigger = (t: MasterEmail) => {
    if ((t.triggerType || 'on_create') === 'day') {
      const days = t.triggerDays ?? 0;
      if (days === 0) return 'Same day as creation';
      return `${days} day${days === 1 ? '' : 's'} after creation`;
    }
    return 'When record created';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        {
          view === 'edit' ?
            <IconButton onClick={() => view === 'edit' ? handleBackToList() : navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            : null
        }
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            flexGrow: 1,
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            },
          }}
        >
          {view === 'list' ? 'Sequence Master Email' : selectedTemplate ? `Edit: ${selectedTemplate.title}` : 'Add New Sequence Master Email'}
        </Typography>
        {view === 'list' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ borderRadius: 2 }}
          >
            Add New Sequence Master Email
          </Button>
        )}
      </Box>

      {view === 'list' ? (
        /* List View */
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '50px' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Template Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Subject Preview</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Trigger</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={templates.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {templates.length > 0 ? (
                      templates.map((template) => (
                        <SortableRow key={template.id} id={template.id}>
                          {/* Drag Handle Column */}
                          <TableCell className="drag-handle" style={{ width: '50px', cursor: 'grab' }}>
                            <DragIndicatorIcon color="action" />
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="600">{template.title}</Typography>
                            {template.description && (
                              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic', maxWidth: '200px' }}>
                                {template.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                              ID: {template.slug}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="textSecondary" sx={{
                              maxWidth: '300px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {template.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={(template.triggerType || 'on_create') === 'day'
                                ? <DayIcon sx={{ fontSize: 14 }} />
                                : <OnCreateIcon sx={{ fontSize: 14 }} />}
                              label={describeTrigger(template)}
                              size="small"
                              color={(template.triggerType || 'on_create') === 'day' ? 'secondary' : 'primary'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditClick(template)}
                              sx={{ borderRadius: 2, mr: 1 }}
                            >
                              Edit & Preview
                            </Button>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(template)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </SortableRow>
                      ))
                    ) : (
                      !isLoading && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <Box
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  p: 2,
                                  borderRadius: '50%',
                                  mb: 2,
                                  color: theme.palette.primary.main
                                }}
                              >
                                <ListIcon sx={{ fontSize: 40 }} />
                              </Box>
                              <Typography variant="h6" color="text.primary" gutterBottom>
                                No master emails found
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 400 }}>
                                You haven't created any master emails yet. Create your first one to start sending automated emails when a record is created or after a number of days.
                              </Typography>
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddNew}
                                sx={{ borderRadius: 2 }}
                              >
                                Add New Sequence Master Email
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </SortableContext>
                </DndContext>
              </TableBody>
            </Table>
          </TableContainer>
          {isLoading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.background.paper, 0.7),
              zIndex: 2,
              borderRadius: 3
            }}>
              <CircularProgress color="primary" />
            </Box>
          )}
        </Paper>
      ) : (
        /* Editor View */
        <Card elevation={3} sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Template Content
                </Typography>
              </Box>
              <Box>
                <Button
                  variant={editorLayout === 'full-preview' ? 'contained' : 'outlined'}
                  startIcon={<PreviewIcon />}
                  onClick={() => setEditorLayout(editorLayout === 'full-preview' ? 'split' : 'full-preview')}
                  sx={{ mr: 1 }}
                  color={editorLayout === 'full-preview' ? 'secondary' : 'primary'}
                >
                  {editorLayout === 'full-preview' ? 'Exit Full Preview' : 'Full Preview Mode'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SendIcon />}
                  onClick={handleOpenTestEmailDialog}
                  sx={{ mr: 1 }}
                >
                  Send Test Email
                </Button>
                <Button
                  variant="contained"
                  startIcon={submitLoader ? <CircularProgress size={20} color="inherit" /> : <CopyIcon />}
                  onClick={handleSave}
                  disabled={submitLoader}
                >
                  {submitLoader ? 'Saving...' : 'Save Sequence Master Email'}
                </Button>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Internal Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Give a brief internal description of this email"
                  helperText="Used to identify the purpose of this email internally"
                  sx={{ mb: 2 }}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Subject and CTA Configuration Panel */}
              {editorLayout === 'split' && (
                <Grid item xs={12} md={6} style={{ height: 700, overflow: 'auto', paddingRight: 8 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Template Name"
                        value={templateTitle}
                        onChange={(e) => {
                          setTemplateTitle(e.target.value);
                          if (formErrors.templateTitle) {
                            setFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.templateTitle;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder="e.g., Welcome Email"
                        helperText={formErrors.templateTitle || "Internal name for this template"}
                        error={!!formErrors.templateTitle}
                        sx={{ mb: 2 }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Unique Identifier (Slug)"
                        value={slug}
                        onChange={(e) => {
                          setSlug(e.target.value);
                          if (formErrors.slug) {
                            setFormErrors(prev => {
                              const newErrors = { ...prev };
                              delete newErrors.slug;
                              return newErrors;
                            });
                          }
                        }}
                        placeholder="e.g., registration_email"
                        helperText={formErrors.slug || "Unique ID for system usage"}
                        error={!!formErrors.slug}
                        disabled={!!selectedTemplate}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Email Subject"
                            value={subject}
                            onChange={(e) => {
                              setSubject(e.target.value);
                              if (formErrors.subject) {
                                setFormErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.subject;
                                  return newErrors;
                                });
                              }
                            }}
                            variant="outlined"
                            placeholder="Enter subject line..."
                            helperText={formErrors.subject || "You can use {{variables}} in the subject too."}
                            error={!!formErrors.subject}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Layout Header Title"
                            value={layoutTitle}
                            onChange={(e) => setLayoutTitle(e.target.value)}
                            variant="outlined"
                            placeholder="e.g. Welcome to JobHunter 🎉"
                            helperText="You can use {{variables}} in the subject too."
                            sx={{ mb: 2, mt: 2 }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Trigger Configuration Panel */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: alpha(theme.palette.primary.main, 0.1),
                          backgroundColor: alpha(theme.palette.primary.main, 0.01),
                          borderRadius: 2,
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <TriggerIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
                          <Typography variant="subtitle2" fontWeight="700">
                            Send Trigger
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Choose when this email should be sent relative to a record.
                        </Typography>

                        <RadioGroup
                          value={triggerType}
                          onChange={(e) => {
                            setTriggerType(e.target.value as 'on_create' | 'day');
                            if (formErrors.triggerDays) {
                              setFormErrors(prev => {
                                const newErrors = { ...prev };
                                delete newErrors.triggerDays;
                                return newErrors;
                              });
                            }
                          }}
                        >
                          <FormControlLabel
                            value="on_create"
                            control={<Radio size="small" />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <OnCreateIcon fontSize="small" color="action" />
                                <Typography variant="body2">When record is created</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            value="day"
                            control={<Radio size="small" />}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <DayIcon fontSize="small" color="action" />
                                <Typography variant="body2">A number of days after creation</Typography>
                              </Box>
                            }
                          />
                        </RadioGroup>

                        {triggerType === 'day' && (
                          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              size="small"
                              type="number"
                              label="Days after creation"
                              value={triggerDays}
                              onChange={(e) => {
                                setTriggerDays(e.target.value);
                                if (formErrors.triggerDays) {
                                  setFormErrors(prev => {
                                    const newErrors = { ...prev };
                                    delete newErrors.triggerDays;
                                    return newErrors;
                                  });
                                }
                              }}
                              error={!!formErrors.triggerDays}
                              helperText={formErrors.triggerDays || 'Use 0 to send on the same day as creation'}
                              InputProps={{ inputProps: { min: 0 } }}
                              sx={{ width: 220 }}
                            />
                          </Box>
                        )}
                      </Paper>
                    </Grid>

                    {/* Simple Email toggle */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: alpha(theme.palette.info.main, 0.2),
                          backgroundColor: alpha(theme.palette.info.main, 0.01),
                          borderRadius: 2,
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight="700">
                          Simple Email (No Layout)
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={isSimpleEmail}
                              onChange={(e) => setIsSimpleEmail(e.target.checked)}
                              color="info"
                            />
                          }
                          label={<Typography variant="caption" fontWeight="medium">Enable</Typography>}
                        />
                      </Paper>
                    </Grid>

                    {/* CTA Button Configuration Panel */}
                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderColor: alpha(theme.palette.secondary.main, 0.2),
                          backgroundColor: alpha(theme.palette.secondary.main, 0.01),
                          borderRadius: 2,
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showCTA ? 2 : 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ButtonIcon color="secondary" sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight="700">
                              Integrated Call-to-Action Button
                            </Typography>
                          </Box>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={showCTA}
                                onChange={(e) => setShowCTA(e.target.checked)}
                                color="secondary"
                              />
                            }
                            label={<Typography variant="caption" fontWeight="medium">Enable Button</Typography>}
                          />
                        </Box>

                        {showCTA && (
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Button Text"
                                value={buttonConfig.text}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, text: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Button Link"
                                value={buttonConfig.url}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, url: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Margin Top"
                                value={buttonConfig.marginTop}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, marginTop: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Margin Bottom"
                                value={buttonConfig.marginBottom}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, marginBottom: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Width"
                                value={buttonConfig.width}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, width: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Height"
                                value={buttonConfig.height}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, height: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Radius"
                                value={buttonConfig.borderRadius}
                                onChange={(e) => setButtonConfig({ ...buttonConfig, borderRadius: e.target.value })}
                              />
                            </Grid>
                            <Grid item xs={4}>
                              <FormControl fullWidth size="small">
                                <InputLabel>Alignment</InputLabel>
                                <Select
                                  value={buttonConfig.alignment}
                                  label="Alignment"
                                  onChange={(e) => setButtonConfig({ ...buttonConfig, alignment: e.target.value as any })}
                                >
                                  <MenuItem value="left">Left</MenuItem>
                                  <MenuItem value="center">Center</MenuItem>
                                  <MenuItem value="right">Right</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="textSecondary" display="block">BG Color</Typography>
                                <input
                                  type="color"
                                  value={buttonConfig.bgColor}
                                  onChange={(e) => setButtonConfig({ ...buttonConfig, bgColor: e.target.value })}
                                  style={{ width: '100%', height: '32px', padding: 0, border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box>
                                <Typography variant="caption" color="textSecondary" display="block">Text Color</Typography>
                                <input
                                  type="color"
                                  value={buttonConfig.textColor}
                                  onChange={(e) => setButtonConfig({ ...buttonConfig, textColor: e.target.value })}
                                  style={{ width: '100%', height: '32px', padding: 0, border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                                />
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ px: 1 }}>
                                <Typography variant="caption" color="textSecondary" gutterBottom display="block">
                                  Vertical Position ({buttonConfig.vPosition === 0 ? 'Top' : buttonConfig.vPosition === 100 ? 'Bottom' : `${buttonConfig.vPosition}%`})
                                </Typography>
                                <Slider
                                  size="small"
                                  value={buttonConfig.vPosition}
                                  onChange={(_, val) => setButtonConfig({ ...buttonConfig, vPosition: val as number })}
                                  color="secondary"
                                  valueLabelDisplay="auto"
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Paper>
                    </Grid>

                    {/* Variables & Editor Toolbar */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                        <Typography variant="body2" color="textSecondary">Insert:</Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VariableIcon />}
                          onClick={handleVariableClick}
                          sx={{ textTransform: 'none' }}
                        >
                          Dynamic Variable
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={openVariableMenu}
                          onClose={handleVariableClose}
                        >
                          {dynamicVariables.map((v) => (
                            <MenuItem key={v.value} onClick={() => insertVariable(v.value)}>
                              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
                              <ListItemText primary={v.name} secondary={`{{${v.value}}}`} />
                            </MenuItem>
                          ))}
                        </Menu>
                      </Box>

                      <Box sx={{ mb: 2, width: '100%' }}>
                        <Typography variant="body2" color={formErrors.content ? "error" : "textSecondary"} sx={{ mb: 1, fontWeight: formErrors.content ? '600' : 'normal' }}>
                          Email Content {formErrors.content && `(${formErrors.content})`}
                        </Typography>
                        <Box sx={{
                          border: formErrors.content ? `1.5px solid ${theme.palette.error.main}` : 'none',
                          borderRadius: '12px',
                          overflow: 'hidden'
                        }}>
                          <RichTextEditor
                            ref={quillRef}
                            value={content}
                            onChange={(val: string) => {
                              setContent(val);
                              if (formErrors.content) {
                                setFormErrors(prev => {
                                  const newErrors = { ...prev };
                                  delete newErrors.content;
                                  return newErrors;
                                });
                              }
                            }}
                            placeholder="Draft your email content here..."
                          />
                        </Box>
                        {formErrors.content && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1 }}>
                            {formErrors.content}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              )}

              {/* Preview Side */}
              <Grid item xs={12} md={editorLayout === 'full-preview' ? 12 : 6} style={{ height: 700, overflow: 'auto', paddingRight: 8 }}>
                <Box sx={{ position: editorLayout === 'split' ? 'sticky' : 'static', top: 20 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Email Preview {editorLayout === 'full-preview' && '(Full Desktop View)'}
                    </Typography>
                    {editorLayout === 'full-preview' && (
                      <Button size="small" onClick={() => setEditorLayout('split')} startIcon={<EditIcon />}>
                        Return to Side-by-Side
                      </Button>
                    )}
                  </Box>
                  {renderLivePreview()}
                </Box>
              </Grid>
            </Grid>
          </CardContent >
        </Card >
      )}

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onClose={handleCloseTestEmailDialog}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 400 }}>
          <Typography variant="body2" color="textSecondary" paragraph>
            Enter an email address to receive a preview of this template.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Recipient Email"
            type="email"
            fullWidth
            variant="outlined"
            value={testEmailAddress}
            onChange={(e) => setTestEmailAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTestEmailDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSendTestEmailConfirm} variant="contained" disabled={isSendingTest}>
            {isSendingTest ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete the master email <strong>"{templateToDelete?.title}"</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={submitLoader}
            startIcon={submitLoader ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {submitLoader ? 'Deleting...' : 'Delete Sequence Master Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Global Submit Loader */}
      <Backdrop
        sx={{ color: '#fff', zIndex: 9999 }}
        open={submitLoader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box >
  );
};

export default MasterEmailPage;
